import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, MessagesSquare, BookOpen, Search, Wrench, Clock3, History, ChevronDown, ChevronUp, MessageSquare, Eye, ThumbsUp, ThumbsDown, Flame } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { PageHeader } from '../components/SectionHeader';
import { CategoryTag } from '../components/Badges';
import {
  MANUALS, TOOLS, MANUALS_LAST_CHECK, POPULAR_SEARCHES, searchForum,
} from '../data/forum';
import { UserName } from '../components/MessagingContext';
import { useI18n } from '../i18n';
import { Button } from '../components/ui/button';
import { ForumComposer, ForumReplyForm, AttachmentList } from '../components/ForumComposer';
import { TechnicalResources } from '../components/TechnicalResources';
import { useForumCatalog } from '../components/ForumContext';
import { useForumData, forumApi } from '../lib/forum';

const LOCALE_MAP = { es: 'es-ES', ca: 'ca-ES', en: 'en-GB' };
const formatDate = (iso, locale) => new Date(iso).toLocaleDateString(LOCALE_MAP[locale] || 'es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
const formatDateTime = (iso, locale) => new Date(iso).toLocaleString(LOCALE_MAP[locale] || 'es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const Highlight = ({ text, terms }) => {
  if (!terms?.length || !text) return <>{text}</>;
  const re = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'ig');
  return <>{text.split(re).map((part, i) => (re.test(part) ? <mark key={i} className="bg-amber-500/30 text-amber-200 rounded px-0.5">{part}</mark> : part))}</>;
};

const ForumPager = ({ page, setPage, total, perPage = 20 }) => total > perPage && <div className="flex flex-wrap items-center justify-center gap-3 mt-6 mb-8"><Button variant="outline" data-testid="forum-page-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button><span data-testid="forum-page-number" className="text-xs text-slate-400">{page} / {Math.ceil(total / perPage)}</span><Button variant="outline" data-testid="forum-page-next" disabled={page * perPage >= total} onClick={() => setPage((p) => p + 1)}>Siguiente</Button></div>;

const ForumLoading = ({ error }) => <div className="min-h-screen bg-[#0B0C0E]"><Navbar /><div className="max-w-4xl mx-auto py-20 px-4"><p data-testid="forum-load-status" role={error ? 'alert' : 'status'} className={error ? 'text-red-300' : 'text-slate-400'}>{error || 'Cargando foro…'}</p><Link data-testid="forum-loading-back" to="/foro" className="text-amber-500 text-sm mt-5 inline-block">Volver al foro</Link></div><Footer /></div>;

const ForumThreadList = ({ category = '', brand = '' }) => {
  const { locale } = useI18n();
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [category, brand]);
  const { data, loading, error } = useForumData(`/threads?category=${encodeURIComponent(category)}&brand=${encodeURIComponent(brand)}&page=${page}`);
  return <div>{loading && <p data-testid="forum-list-loading" className="text-sm text-slate-400">Cargando hilos…</p>}{error && <p role="alert" data-testid="forum-list-error" className="text-sm text-red-300">{error}</p>}<div className="space-y-3">{data?.items.map((thread) => <ThreadRow key={thread.id} thread={thread} locale={locale} />)}</div>{!loading && !error && !data?.items.length && <p data-testid="forum-list-empty" className="sc-card p-6 text-sm text-slate-400">Aún no hay hilos. Comparte tu primera pregunta.</p>}<ForumPager page={page} setPage={setPage} total={data?.total || 0} /></div>;
};

export const ForumSearch = ({ compact = false }) => {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const { locale } = useI18n();
  const staticResults = useMemo(() => searchForum(q), [q]);
  const remote = useForumData(q.length >= 2 ? `/threads?q=${encodeURIComponent(q)}` : null);
  const addedResources = useForumData(q.length >= 2 ? `/resources?q=${encodeURIComponent(q)}` : null);
  const results = { ...staticResults, threads: remote.data?.items || [],
    manuals: [...staticResults.manuals, ...(addedResources.data || []).filter((r) => r.kind === 'manual')],
    tools: [...staticResults.tools, ...(addedResources.data || []).filter((r) => r.kind !== 'manual').map((r) => ({ ...r, name: r.title, vendor: r.brand }))],
  };
  const setQ = (v) => { const p = new URLSearchParams(params); if (v) p.set('q', v); else p.delete('q'); setParams(p, { replace: true }); };
  const total = results.threads.length + results.manuals.length + results.tools.length;
  return (
    <div className="mb-8" data-testid="forum-search">
      <div className="sc-card p-3 flex items-center gap-3 focus-within:border-amber-500">
        <Search size={18} className="text-amber-500 shrink-0 ml-1" />
        <input
          data-testid="forum-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar en el foro: macro grandMA3, timecode, factura autónomo, Dante, IRATA…"
          className="flex-1 min-w-0 bg-transparent outline-none text-white text-sm py-2"
        />
        {q && <button type="button" data-testid="forum-search-clear" onClick={() => setQ('')} className="text-[11px] font-mono uppercase tracking-widest text-slate-500 hover:text-amber-500 px-2">Limpiar</button>}
      </div>
      {!q && !compact && (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Populares:</span>
          {POPULAR_SEARCHES.map((s) => <button key={s} type="button" data-testid={`forum-popular-${s.replace(/\s+/g, '-')}`} onClick={() => setQ(s)} className="text-[11px] font-mono px-2.5 py-1 rounded-full border border-[#2A2F3D] text-slate-400 hover:text-amber-500 hover:border-amber-500/40 transition-colors">{s}</button>)}
        </div>
      )}
      {q.length >= 2 && (
        <div className="mt-4 space-y-6" data-testid="forum-search-results">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500" data-testid="forum-search-count">{total} resultados para «{q}»{results.terms.length > 1 && <span className="text-slate-600"> · también busco: {results.terms.filter((t) => !q.toLowerCase().includes(t)).join(', ')}</span>}</div>
          {results.threads.length > 0 && (
            <div>
              <h3 className="sc-mono-eyebrow text-amber-500 mb-3">Hilos ({results.threads.length})</h3>
              <div className="space-y-3">{results.threads.map((t) => <ThreadRow key={t.id} thread={t} locale={locale} terms={results.terms} />)}</div>
            </div>
          )}
          {results.manuals.length > 0 && (
            <div>
              <h3 className="sc-mono-eyebrow text-amber-500 mb-3">Manuales ({results.manuals.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{results.manuals.map((m) => <ManualCard key={m.id} manual={m} />)}</div>
            </div>
          )}
          {results.tools.length > 0 && (
            <div>
              <h3 className="sc-mono-eyebrow text-amber-500 mb-3">Herramientas ({results.tools.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{results.tools.map((t) => <ToolCard key={t.id} tool={t} testid={`tool-${t.id}`} />)}</div>
            </div>
          )}
          {remote.error && <p role="alert" data-testid="forum-search-error" className="text-sm text-red-300">{remote.error}</p>}
          {total === 0 && !remote.loading && !remote.error && <div className="sc-card p-6 text-sm text-slate-400 text-center" data-testid="forum-search-empty">Nada por aquí. Prueba con la marca (Avolites, DiGiCo…), el tema (timecode, factura) o una palabra clave más corta.</div>}
        </div>
      )}
    </div>
  );
};

const SubNav = ({ current }) => (
  <div className="flex flex-wrap min-w-0 gap-2 pb-3 mb-4 max-w-full" data-testid="forum-subnav">
    <SubLink to="/foro" label="Categorías" icon={MessagesSquare} active={current === 'root'} />
    <SubLink to="/foro/manuales" label="Manuales" icon={BookOpen} active={current === 'manuals'} />
    <SubLink to="/foro/herramientas" label="Fixture finder & plugins" icon={Wrench} active={current === 'tools'} />
    <SubLink to="/foro/legal" label="Legal & Administración" icon={BookOpen} active={current === 'legal'} />
  </div>
);

const SubLink = ({ to, label, icon: Icon, active }) => (
  <Link
    to={to}
    data-testid={`forum-subnav-${to.split('/').pop()}`}
    className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-widest rounded-full border whitespace-nowrap transition-colors ${
      active ? 'bg-amber-500 text-slate-950 border-amber-500' : 'text-slate-300 border-[#2A2F3D] hover:border-amber-500/40'
    }`}
  >
    <Icon size={12} />
    {label}
  </Link>
);

export const ForumIndexPage = () => {
  const { categories, loading, error } = useForumCatalog();
  return (
  <div className="min-h-screen bg-[#0B0C0E]">
    <Navbar />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <PageHeader
        title="Foro y biblioteca técnica"
        subtitle="Dudas resueltas, trucos de programación, manuales y buscadores oficiales. Todo por marca y sector."
        testId="forum-header"
      />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6"><SubNav current="root" /><ForumComposer /></div>
      <ForumSearch />
      {loading && <p data-testid="forum-categories-loading" className="text-sm text-slate-400 mb-6">Cargando categorías…</p>}
      {error && <p role="alert" data-testid="forum-categories-error" className="text-sm text-red-300 mb-6">{error}</p>}
      <MostViewed />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-16">
        {categories.map((c) => (
          <Link key={c.id} to={`/foro/${c.id}`} data-testid={`forum-cat-${c.id}`} className="sc-card p-6 flex flex-col group">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.accent, boxShadow: `0 0 12px ${c.accent}` }} />
              <span data-testid={`forum-category-count-${c.id}`} className="sc-mono-eyebrow text-slate-500">{c.threads} hilos · {c.brands.length} marcas</span>
            </div>
            <h3 className="text-xl font-semibold text-white group-hover:text-amber-500 transition-colors mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.name}</h3>
            <div className="flex flex-wrap gap-1.5 mt-2 mb-4">
              {c.brands.slice(0, 4).map((b) => (
                <span key={b.id} className="text-[11px] font-mono px-2 py-0.5 rounded border border-[#2A2F3D] text-slate-400">{b.name}</span>
              ))}
              {c.brands.length > 4 && <span className="text-[11px] font-mono text-slate-500">+{c.brands.length - 4}</span>}
            </div>
            <span className="mt-auto text-xs font-mono uppercase tracking-wider text-amber-500 group-hover:translate-x-1 transition-transform">Ver hilos →</span>
          </Link>
        ))}
      </div>
    </div>
    <Footer />
  </div>
  );
};

export const ForumCategoryPage = () => {
  const { category } = useParams();
  const { findCategory, loading, error } = useForumCatalog();
  const cat = findCategory(category);
  if (loading || error) return <ForumLoading error={error} />;
  if (!cat) return <ForumNotFound />;

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-8">
          <Link to="/foro" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-slate-400 hover:text-amber-500 mb-4">
            <ArrowLeft size={14} /> Foro
          </Link>
        </div>
        <PageHeader title={cat.name} subtitle={`${cat.brands.length} marcas · ${cat.threads} hilos activos`} testId={`forum-cat-header-${cat.id}`} />
        <div className="flex flex-wrap justify-between gap-4 mb-6"><SubNav current="root" /><ForumComposer category={category} /></div>
        <ForumSearch compact />

        <div>
          <h3 className="sc-mono-eyebrow text-amber-500 mb-4">Marcas</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-12">
            {cat.brands.map((b) => (
              <Link
                key={b.id}
                to={`/foro/${cat.id}/${b.id}`}
                data-testid={`forum-brand-${b.id}`}
                className="sc-card p-4 flex flex-col items-start gap-1 group"
              >
                <span className="text-sm font-medium text-white group-hover:text-amber-500 transition-colors">{b.name}</span>
                <span data-testid={`forum-brand-count-${b.id}`} className="text-[11px] text-slate-500 font-mono uppercase tracking-widest">{b.threads} hilos</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h3 className="sc-mono-eyebrow text-amber-500 mb-4">Hilos recientes</h3>
          <ForumThreadList category={category} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

const ThreadRow = ({ thread, locale, terms }) => {
  const { findCategory, findBrand } = useForumCatalog();
  const cat = findCategory(thread.category);
  const brand = findBrand(thread.category, thread.brand);
  const replies = thread.posts?.length ?? thread.replies;
  const last = thread.posts?.length ? thread.posts[thread.posts.length - 1] : null;
  return (
    <article data-testid={`forum-thread-${thread.id}`} className="sc-card p-5 flex flex-col md:flex-row md:items-center gap-4 group">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {cat && <CategoryTag name={cat.name} accent={cat.accent} />}
          {brand && <span className="text-[11px] font-mono px-2 py-0.5 rounded border border-[#2A2F3D] text-slate-400">{brand.name}</span>}
          <span className="text-[10px] text-slate-500 font-mono">Comunidad</span>
        </div>
        <h4 className="text-base font-semibold text-white group-hover:text-amber-500 transition-colors leading-snug [overflow-wrap:anywhere]" style={{ fontFamily: 'Outfit, sans-serif' }}><Link data-testid={`forum-thread-link-${thread.id}`} to={`/foro/hilo/${thread.id}`}><Highlight text={thread.title} terms={terms} /></Link></h4>
        <p className="text-sm text-slate-400 mt-1 line-clamp-1"><Highlight text={thread.summary} terms={terms} /></p>
      </div>
      <div className="text-right shrink-0 flex md:flex-col md:items-end gap-3 md:gap-0.5 text-[11px] font-mono uppercase tracking-widest text-slate-500">
        <span className="inline-flex items-center gap-1"><MessageSquare size={11} /> {replies} respuestas</span>
        <span><UserName userId={thread.author_id} testId={`thread-row-author-${thread.id}`}>{thread.author}</UserName> · {formatDateTime(thread.date, locale)}</span>
        {last && <span className="text-slate-600 normal-case tracking-normal">Último: {last.author} · {formatDateTime(last.at, locale)}</span>}
      </div>
    </article>
  );
};

export const ForumBrandPage = () => {
  const { category, brand } = useParams();
  const { findCategory, findBrand, loading, error } = useForumCatalog();
  const cat = findCategory(category);
  const br = findBrand(category, brand);
  if (loading || error) return <ForumLoading error={error} />;
  if (!cat || !br) return <ForumNotFound />;
  const relatedManuals = MANUALS.filter((m) => m.category === category && m.brand.toLowerCase().includes(br.name.split(' ')[0].toLowerCase()));

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-8">
          <Link to={`/foro/${category}`} className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-slate-400 hover:text-amber-500 mb-4">
            <ArrowLeft size={14} /> {cat.name}
          </Link>
        </div>
        <PageHeader title={br.name} subtitle={`${br.threads} hilos activos · ${relatedManuals.length} manuales oficiales`} testId={`forum-brand-header-${br.id}`} />
        <div className="flex flex-wrap justify-between gap-4 mb-6"><SubNav current="root" /><ForumComposer category={category} brand={brand} /></div>

        {relatedManuals.length > 0 && (
          <div className="mb-10">
            <h3 className="sc-mono-eyebrow text-amber-500 mb-4">Manuales oficiales</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {relatedManuals.map((m) => <ManualCard key={m.id} manual={m} />)}
            </div>
          </div>
        )}

        <div className="mb-16">
          <h3 className="sc-mono-eyebrow text-amber-500 mb-4">Hilos</h3>
          <ForumThreadList category={category} brand={brand} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

const ManualCard = ({ manual }) => {
  const [open, setOpen] = useState(false);
  const { locale } = useI18n();
  const previous = (manual.versions || []).slice(1);
  return (
    <div className="sc-card p-5 flex flex-col gap-3" data-testid={`manual-${manual.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/40 flex items-center justify-center text-amber-500 shrink-0">
          <BookOpen size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-1">{manual.brand} · <span className="text-emerald-400">v{manual.version} actual</span> · {formatDate(manual.date, locale)}</div>
          <a href={manual.url} target="_blank" rel="noopener noreferrer" data-testid={`manual-link-${manual.id}`} className="text-sm font-medium text-white hover:text-amber-500 transition-colors leading-snug inline-flex items-center gap-1.5">
            {manual.title} <ExternalLink size={12} className="text-slate-500 shrink-0" />
          </a>
          <div className="text-[11px] text-slate-500 font-mono mt-1">{manual.size}</div>
        </div>
      </div>
      {previous.length > 0 && (
        <div className="pt-3 border-t border-[#2A2F3D]">
          <button type="button" data-testid={`manual-versions-toggle-${manual.id}`} onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-slate-400 hover:text-amber-500 transition-colors">
            <History size={12} /> Versiones anteriores ({previous.length}) {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {open && (
            <ul className="mt-3 space-y-1.5" data-testid={`manual-versions-${manual.id}`}>
              {previous.map((v) => (
                <li key={v.version} className="flex items-center justify-between text-xs">
                  <a href={v.url} target="_blank" rel="noopener noreferrer" className="font-mono text-slate-300 hover:text-amber-500 inline-flex items-center gap-1.5">v{v.version} <ExternalLink size={10} className="text-slate-600" /></a>
                  <span className="text-slate-500 font-mono">{formatDate(v.date, locale)}</span>
                </li>
              ))}
              {manual.downloads && <li className="pt-1"><a href={manual.downloads} target="_blank" rel="noopener noreferrer" className="text-[11px] font-mono uppercase tracking-widest text-amber-500 hover:underline">Historial completo en el fabricante ↗</a></li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export const ManualsPage = () => (
  <div className="min-h-screen bg-[#0B0C0E]">
    <Navbar />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <PageHeader
        title="Manuales de usuario"
        subtitle={`Manuales oficiales de mesas de iluminación y sonido con la versión vigente y hasta 5 versiones anteriores. Última comprobación en las webs de los fabricantes: ${formatDate(MANUALS_LAST_CHECK, 'es')}.`}
        testId="manuals-header"
      />
      <SubNav current="manuals" />
      <TechnicalResources kind="manual" />
      <ForumSearch compact />

      {[['iluminacion', 'Iluminación'], ['sonido', 'Sonido'], ['video', 'Vídeo / AV / Playback']]
        .filter(([cat]) => MANUALS.some((m) => m.category === cat))
        .map(([cat, label]) => (
        <section key={cat} className="mb-14">
          <h3 className="sc-mono-eyebrow text-amber-500 mb-4">{label}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {MANUALS.filter((m) => m.category === cat).map((m) => <ManualCard key={m.id} manual={m} />)}
          </div>
        </section>
      ))}
    </div>
    <Footer />
  </div>
);

export const ToolsPage = () => (
  <div className="min-h-screen bg-[#0B0C0E]">
    <Navbar />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <PageHeader
        title="Fixture finder & plugin search"
        subtitle="Buscadores oficiales de fixtures DMX para iluminación y de plugins para sonido. Todo en un solo panel."
        testId="tools-header"
      />
      <SubNav current="tools" />

      <section className="mb-14">
        <div className="flex items-center gap-2 mb-4">
          <Search size={16} className="text-amber-500" />
          <h3 className="sc-mono-eyebrow text-amber-500">Fixture finders · Iluminación</h3>
        </div>
        <TechnicalResources kind="fixture" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TOOLS.fixtures.map((tool) => <ToolCard key={tool.id} tool={tool} testid={`tool-${tool.id}`} />)}
        </div>
      </section>

      <section className="mb-16">
        <div className="flex items-center gap-2 mb-4">
          <Search size={16} className="text-amber-500" />
          <h3 className="sc-mono-eyebrow text-amber-500">Búsqueda de plugins · Sonido</h3>
        </div>
        <TechnicalResources kind="plugin" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TOOLS.plugins.map((tool) => <ToolCard key={tool.id} tool={tool} testid={`tool-${tool.id}`} />)}
        </div>
      </section>
    </div>
    <Footer />
  </div>
);

const ToolCard = ({ tool, testid }) => (
  <a
    href={tool.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="sc-card p-5 flex items-start gap-4 group"
  >
    <div className="w-11 h-11 rounded-lg bg-[#1A1D24] border border-[#2A2F3D] flex items-center justify-center text-amber-500 shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
      <Search size={18} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-semibold text-white group-hover:text-amber-500 transition-colors">{tool.name}</span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 border border-[#2A2F3D] px-1.5 py-0.5 rounded">{tool.vendor}</span>
      </div>
      <p className="text-sm text-slate-400 leading-relaxed line-clamp-2">{tool.description}</p>
      <div className="mt-2 flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-amber-500">
        Abrir en nueva pestaña <ExternalLink size={12} />
      </div>
    </div>
  </a>
);

export const ForumThreadPage = () => {
  const { locale } = useI18n();
  const { id } = useParams();
  const { data: thread, loading, error } = useForumData(`/threads/${id}`);
  const [replyPage, setReplyPage] = useState(1);
  const { data: replies, refresh: refreshReplies, error: replyError } = useForumData(`/threads/${id}/replies?page=${replyPage}`);
  const { findCategory, findBrand } = useForumCatalog();
  const th = useMemo(() => thread ? { ...thread, posts: replies?.items || [], replies: replies?.total || 0 } : null, [thread, replies]);
  const [stats, setStats] = useState({ views: 0, votes: {} });
  useEffect(() => {
    if (thread?.id) forumApi(`/threads/${thread.id}/view`, { method: 'POST' }).then((d) => setStats((s) => ({ ...s, views: d.views }))).catch(() => {});
  }, [thread?.id]);
  useEffect(() => {
    if (!thread?.id) return;
    const ids = (replies?.items || []).map((p) => p.id).join(',');
    forumApi(`/threads/${thread.id}/stats?post_ids=${ids}`).then(setStats).catch(() => {});
  }, [thread?.id, replies]);
  const vote = async (postId, value) => {
    const res = await fetch(`${API}/forum/posts/${postId}/vote`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ value }) });
    if (!res.ok) { toast.error('Inicia sesión para votar'); return; }
    const v = await res.json();
    setStats((s) => ({ ...s, votes: { ...s.votes, [postId]: v } }));
  };
  if (loading || error) return <ForumLoading error={error} />;
  if (!th) return <ForumNotFound />;
  const cat = findCategory(th.category);
  const brand = findBrand(th.category, th.brand);
  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <Link to={`/foro/${th.category}`} className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-slate-400 hover:text-amber-500 mb-8">
          <ArrowLeft size={14} /> {cat?.name}
        </Link>
        <div className="flex flex-wrap gap-2 mb-4">
          {cat && <CategoryTag name={cat.name} accent={cat.accent} />}
          {brand && <span className="text-[11px] font-mono px-2 py-0.5 rounded border border-[#2A2F3D] text-slate-400">{brand.name}</span>}
          <span className="text-[10px] text-slate-500 font-mono">Comunidad</span>
        </div>
        <h1 data-testid="forum-thread-heading" className="text-3xl md:text-4xl font-bold text-white leading-tight mb-3 [overflow-wrap:anywhere]" style={{ fontFamily: 'Outfit, sans-serif' }}>{th.title}</h1>
        <div className="text-sm text-slate-500 font-mono uppercase tracking-wider mb-8 flex flex-wrap items-center gap-2" data-testid="thread-meta">
          <UserName userId={th.author_id} testId="thread-author-meta">{th.author}</UserName><span>·</span>
          <span className="inline-flex items-center gap-1"><Clock3 size={12} /> {formatDateTime(th.date, locale)}</span><span>·</span>
          <span data-testid="forum-replies-count">{th.replies} respuestas</span><span>·</span>
          <span className="inline-flex items-center gap-1 text-amber-500" data-testid="thread-views"><Eye size={12} /> {stats.views} visitas</span>
        </div>
        <div className="sc-card p-6 md:p-8 mb-8" data-testid="thread-original-post">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div className="flex items-center gap-3">
              <Avatar name={th.author} />
              <div>
                <div className="text-sm font-semibold text-white"><UserName userId={th.author_id} testId="thread-author-name">{th.author}</UserName></div>
                <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">Abrió el hilo</div>
              </div>
            </div>
            <span className="text-[11px] text-slate-500 font-mono inline-flex items-center gap-1"><Clock3 size={11} /> {formatDateTime(th.date, locale)}</span>
          </div>
          <p data-testid="forum-thread-text" className="text-slate-300 leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">{th.body}</p>
          <AttachmentList items={th.attachments} testId="thread-attach" />
          {th.tags?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-4">{th.tags.map((tg) => <span key={tg} className="text-[11px] font-mono px-2 py-0.5 rounded border border-[#2A2F3D] text-slate-400">#{tg}</span>)}</div>}
        </div>

        <section data-testid="thread-posts">
          <h2 className="sc-mono-eyebrow text-amber-500 mb-4 flex items-center gap-2"><MessageSquare size={13} /> Comentarios ({th.replies})</h2>
          {(!th.posts || th.posts.length === 0) && <div className="sc-card p-6 text-sm text-slate-400 text-center" data-testid="thread-posts-empty">Todavía nadie ha respondido. Comparte tu experiencia.</div>}
          <div className="relative">
            {th.posts?.length > 1 && <div className="absolute left-[19px] top-6 bottom-6 w-px bg-[#2A2F3D]" />}
            <div className="space-y-4">
              {(th.posts || []).map((p, i) => (
                <div key={p.id} className="flex gap-4" data-testid={`thread-post-${p.id}`}>
                  <Avatar name={p.author} />
                  <div className="sc-card p-4 md:p-5 flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                      <div>
                        <UserName userId={p.author_id} testId={`thread-reply-author-${p.id}`} className="text-sm font-semibold text-white">{p.author}</UserName>
                        <span className="text-[11px] text-slate-500 font-mono uppercase tracking-wider ml-2">{p.role}</span>
                      </div>
                      <span className="text-[11px] text-slate-500 font-mono inline-flex items-center gap-1" data-testid={`thread-post-time-${p.id}`}><Clock3 size={11} /> {formatDateTime(p.at, locale)} · #{i + 1}</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">{p.body}</p>
                    <AttachmentList items={p.attachments} testId={`reply-attach-${p.id}`} />
                    <VoteBar postId={p.id} v={stats.votes[p.id]} onVote={vote} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {replyError && <p role="alert" data-testid="forum-replies-error" className="text-sm text-red-300">{replyError}</p>}
          <ForumPager page={replyPage} setPage={setReplyPage} total={th.replies} perPage={30} />
          <ForumReplyForm threadId={th.id} onPosted={() => { setReplyPage(Math.floor(th.replies / 30) + 1); refreshReplies(); }} />
        </section>
      </div>
      <Footer />
    </div>
  );
};

const VoteBar = ({ postId, v = {}, onVote }) => (
  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#2A2F3D]" data-testid={`vote-bar-${postId}`}>
    <button type="button" data-testid={`vote-up-${postId}`} onClick={() => onVote(postId, 1)} className={`inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-md border transition-colors ${v.my_vote === 1 ? 'text-emerald-300 border-emerald-500/60 bg-emerald-500/10' : 'text-slate-400 border-[#2A2F3D] hover:text-emerald-300 hover:border-emerald-500/40'}`}><ThumbsUp size={12} /> {v.up || 0}</button>
    <button type="button" data-testid={`vote-down-${postId}`} onClick={() => onVote(postId, -1)} className={`inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-md border transition-colors ${v.my_vote === -1 ? 'text-red-300 border-red-500/60 bg-red-500/10' : 'text-slate-400 border-[#2A2F3D] hover:text-red-300 hover:border-red-500/40'}`}><ThumbsDown size={12} /> {v.down || 0}</button>
    <span className="text-[11px] font-mono text-slate-500 ml-1">{(v.up || 0) - (v.down || 0) >= 0 ? '+' : ''}{(v.up || 0) - (v.down || 0)} útil</span>
  </div>
);

const MostViewed = () => {
  const { locale } = useI18n();
  const { data, error } = useForumData('/threads?sort=views');
  const top = (data?.items || []).slice(0, 5).map((t) => ({ t, v: t.views }));
  return (
    <section className="mb-10" data-testid="forum-most-viewed">
      <div className="flex items-center gap-2 mb-4"><Flame size={14} className="text-amber-500" /><h3 className="sc-mono-eyebrow text-amber-500">Hilos más vistos</h3></div>
      {error && <p role="alert" data-testid="forum-top-error" className="text-sm text-red-300">{error}</p>}
      {data && top.length === 0 && <p data-testid="forum-empty" className="text-sm text-slate-400 mb-4">Aún no hay hilos. Abre la primera conversación con «Crear hilo».</p>}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {top.map(({ t, v }, i) => (
          <Link key={t.id} to={`/foro/hilo/${t.id}`} data-testid={`most-viewed-${t.id}`} className="sc-card p-4 flex flex-col gap-2 group">
            <div className="flex items-center justify-between text-[11px] font-mono text-slate-500"><span>#{i + 1}</span><span className="inline-flex items-center gap-1 text-amber-500"><Eye size={11} /> {v}</span></div>
            <div className="text-sm text-white group-hover:text-amber-500 leading-snug line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.title}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-auto">{t.author} · {formatDate(t.date, locale)}</div>
          </Link>
        ))}
      </div>
    </section>
  );
};

const Avatar = ({ name }) => (
  <div className="w-10 h-10 rounded-full bg-[#1A1D24] border border-[#2A2F3D] flex items-center justify-center text-amber-500 text-xs font-bold shrink-0 relative z-10" style={{ fontFamily: 'Outfit, sans-serif' }}>
    {name.split(' ').map((n) => n[0]).slice(0, 2).join('')}
  </div>
);

const ForumNotFound = () => (
  <div className="min-h-screen bg-[#0B0C0E]">
    <Navbar />
    <div className="max-w-3xl mx-auto px-4 py-24 text-center">
      <h1 className="text-3xl font-bold text-white mb-4">Contenido no encontrado</h1>
      <Link to="/foro" className="text-amber-500 font-mono uppercase tracking-wider text-sm">← Volver al foro</Link>
    </div>
    <Footer />
  </div>
);
